import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { OnboardingService } from '../../../features/onboarding/services/onboarding.service';
import { DashboardService } from '../../../features/dashboard/services/dashboard.service';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { ToastService } from '../../toast/toast.service';
import { NotificationService } from '../../services/notification.service';
import { ProjectService } from '../../services/project.service';
import { ProjectMembershipService } from '../../services/project-membership.service';
import { SessionService } from '../../services/session.service';
import { OrganizationDashboardService } from '../../../features/dashboard/services/organization-dashboard.service';
import { OrgMemberDetailDto } from '../../../features/dashboard/models/organization.dto';
import { ProjectService as ProjectApiService } from '../../../features/dashboard/services/project.service';
import { RequestService } from '../../../features/dashboard/services/request.service';
import { SlackService } from '../../../features/dashboard/services/slack.service';
import { GoogleChatService } from '../../../features/dashboard/services/google-chat.service';
import { WebflowService } from '../../../features/dashboard/services/webflow.service';
import { ProjectMemberDto, ProjectMemberDetailDto } from '../../../features/dashboard/models/project.dto';
import { ProjectsDataDto } from '../../models/dashboard.dto';
import { GoogleChatSpaceDto } from '../../../features/dashboard/models/google-chat.dto';
import { SingleDropdownOption } from '../inputs/declunk-single-dropdown/declunk-single-dropdown.component';
import { DeclunkConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { environment } from 'src/environments/environment';
import { RouterModule } from '@angular/router';

/** View model for one global notification preference row — mirrors NotificationsPanelComponent's NotifPrefVm. */
interface NotifPrefVm {
  type: string;
  label: string;
  description: string;
  enabled: boolean;       // In-app
  emailEnabled: boolean;  // Email
}

@Component({
  selector: 'app-settings-modal',
  imports: [FormsModule, DatePipe, LucideAngularModule, DeclunkConfirmDialogComponent, RouterModule],
  templateUrl: './settings-modal.component.html',
  styleUrl: './settings-modal.component.scss'
})
export class SettingsModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() projects: ProjectsDataDto[] = [];
  @Input() initialSection: 'projects-general' | null = null;
  @Input() initialProjectId?: string;
  @Output() closeModal = new EventEmitter<void>();
  /** Emitted with the project id after it's been deleted or left, so the parent can drop it and redirect. */
  @Output() projectRemoved = new EventEmitter<string>();

  @ViewChild('settingsContent') settingsContentRef?: ElementRef<HTMLElement>;

  public activeSection: 'account' | 'notifications' | 'workspace-general' | 'projects-general' = 'account';

  public firstName = '';
  public lastName = '';

  public passwordFormOpen = false;
  public currentPassword = '';
  public newPassword = '';
  public confirmPassword = '';
  public passwordSubmitting = false;

  public avatarPreview: string | null = null;
  public isUploadingAvatar = false;

  // Notifications section — global notification preferences (In-app / Email per type).
  public settingsPrefs: NotifPrefVm[] = [];
  public notifPrefsLoading = false;
  public notifPrefsError = false;
  private notifPrefsLoaded = false;

  private static readonly INVOLVED_PREF_ORDER = [
    'task.assigned',
    'request.review_requested',
    'request.updated',
    'request.deleted',
    'comment.created',
  ];
  private static readonly GENERAL_PREF_ORDER = [
    'site.published',
    'comment.mention',
    'request.created',
    'project.watch_all',
    'webflow.sync_started',
    'webflow.sync_completed',
    'webflow.sync_failed',
  ];

  // Workspace → General section — icon/name, org members (client-side paginated), join-workspace toggle.
  public workspaceName = '';
  public workspaceIconPreview: string | null = null;
  public isUploadingIcon = false;

  public orgMembers: OrgMemberDetailDto[] = [];
  public membersLoading = false;
  public membersError = false;
  private workspaceGeneralLoaded = false;

  // Editable member-role dropdown (org owner/admin only) — role options (owner excluded),
  // per-row OrgMemberRole id resolution, and per-row saving state.
  public orgRoleOptions: SingleDropdownOption[] = [];
  public orgRolesLoading = false;
  private orgRolesLoaded = false;
  private orgMemberRoleRowIds = new Map<string, string>();
  public savingOrgMemberRoleIds = new Set<string>();

  public pageSize = 4;
  public currentPage = 1;
  public readonly pageSizeOptions = [4, 8, 12, 20];

  public allowDomainJoin = true;
  public domainJoinLoading = false;
  public isUpdatingDomainJoin = false;

  public linkJoinEnabled = false;
  public workspaceInvitationCode: string | null = null;
  public isUpdatingLinkJoin = false;

  // Projects → General section.
  public selectedProjectId: string | null = null;
  public projectName = '';
  public projectIconPreview: string | null = null;
  public isUploadingProjectIcon = false;
  public projectDetailLoading = false;
  public defaultAssigneeId: string | null = null;
  public isSavingDefaultAssignee = false;

  // Full member records — used for the default-assignee dropdown (needs user.user_id/status).
  public projectMembers: ProjectMemberDto[] = [];
  // Table-display rows (name/role/projects_involved/last_active) — from the additive /members/detail/ endpoint.
  public projectMembersDetail: ProjectMemberDetailDto[] = [];
  public projectMembersLoading = false;
  public projectMembersError = false;
  public openMemberMenuId: string | null = null;
  public removeMemberPendingTarget: ProjectMemberDetailDto | null = null;

  // Editable member-role dropdown (project admins only) — role options, per-row ProjectMemberRole
  // id resolution, and per-row saving state.
  public projectRoleOptions: SingleDropdownOption[] = [];
  public projectRolesLoading = false;
  private projectRolesLoaded = false;
  private readonly roleLabels: Record<string, string> = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' };
  private memberRoleRowIds = new Map<string, string>();
  public savingMemberRoleIds = new Set<string>();

  public projectMemberPageSize = 4;
  public projectMemberCurrentPage = 1;

  // Projects → Danger zone: delete project (type-project-name-to-confirm).
  public deleteProjectConfirmOpen = false;
  public deleteProjectConfirmText = '';
  public isDeletingProject = false;
  public deleteProjectError: string | null = null;

  // Projects → Danger zone: leave project (self-service, any role).
  public leaveProjectConfirmOpen = false;
  public isLeavingProject = false;

  // Project invite link — GET/PATCH /api/projects/{id}/link-settings/.
  public projectLinkJoinEnabled = false;
  public projectInvitationCode: string | null = null;
  public isUpdatingProjectLinkJoin = false;
  public isRegeneratingProjectLink = false;

  public readonly projectDefaultRoleOptions: SingleDropdownOption[] = [
    { id: 'member', label: 'Member' },
    { id: 'admin', label: 'Admin' },
    { id: 'viewer', label: 'Viewer' },
  ];
  public projectDefaultRole: 'admin' | 'member' | 'viewer' | null = 'member';
  public isSavingProjectDefaultRole = false;

  // Team & Roles — no backend for these yet, visual-only toggles.
  public autoExpireInviteLinks = true;
  public allowMembersToInvite = true;

  // Integrations.
  public slackConnected = false;
  public slackLoading = false;
  public webflowConnected = false;
  public webflowLoading = false;
  public webflowConnectedSince = '';
  public gchatConnected = false;
  public gchatLoading = false;
  public gchatPickerOpen = false;
  public gchatSpaces: GoogleChatSpaceDto[] = [];
  public gchatSpacesLoading = false;
  public gchatSelectedSpaceName = '';
  public gchatConnecting = false;
  
  public isProd = environment.production;

  constructor(
    private readonly session: SessionService,
    private readonly onboardingService: OnboardingService,
    private readonly dashboardService: DashboardService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly notificationService: NotificationService,
    private readonly projectService: ProjectService,
    private readonly organizationDashboardService: OrganizationDashboardService,
    private readonly projectApiService: ProjectApiService,
    private readonly requestService: RequestService,
    private readonly slackService: SlackService,
    private readonly googleChatService: GoogleChatService,
    private readonly webflowService: WebflowService,
    private readonly projectMembershipService: ProjectMembershipService
  ) { }

  ngOnChanges(): void {
    if (!this.isOpen) return;

    if (this.initialSection === 'projects-general') {
      this.activeSection = 'projects-general';
      const project = this.projects.find((p) => p.id === this.initialProjectId) ?? this.projects[0];
      if (project) {
        this.selectProject(project);
      }
      if (this.settingsContentRef) {
        this.settingsContentRef.nativeElement.scrollTop = 0;
      }
      setTimeout(() => {
        this.settingsContentRef?.nativeElement.querySelector('#integrationsSection')?.scrollIntoView({ block: 'start' });
      });
      return;
    }

    this.activeSection = 'account';
    this.firstName = this.session.profile()?.first_name ?? '';
    this.lastName = this.session.profile()?.last_name ?? '';
    this.avatarPreview = null;
    this.workspaceName = this.session.organizationName();
    this.workspaceIconPreview = null;
  }

  selectSection(section: 'account' | 'notifications' | 'workspace-general' | 'projects-general'): void {
    if (section === 'projects-general' && this.projects.length === 0) return;
    this.activeSection = section;
    if (this.settingsContentRef) {
      this.settingsContentRef.nativeElement.scrollTop = 0;
    }
    if (section === 'notifications' && !this.notifPrefsLoaded) {
      this.loadNotifPrefs();
    }
    if (section === 'workspace-general' && !this.workspaceGeneralLoaded) {
      this.workspaceGeneralLoaded = true;
      this.loadOrgMembers();
      this.loadDomainJoinSetting();
      this.loadOrgRoles();
    }
    if (section === 'projects-general' && !this.selectedProjectId && this.projects.length > 0) {
      this.selectProject(this.projects[0]);
    }
  }

  get groupedSettingsPrefs(): { label: string; prefs: NotifPrefVm[] }[] {
    const orderOf = (order: string[], type: string) => {
      const i = order.indexOf(type);
      return i === -1 ? order.length : i;
    };
    const involvedOrder = SettingsModalComponent.INVOLVED_PREF_ORDER;
    const involved = this.settingsPrefs
      .filter((p) => involvedOrder.includes(p.type))
      .sort((a, b) => orderOf(involvedOrder, a.type) - orderOf(involvedOrder, b.type));
    const generalOrder = SettingsModalComponent.GENERAL_PREF_ORDER;
    const general = this.settingsPrefs
      .filter((p) => !involvedOrder.includes(p.type))
      .sort((a, b) => orderOf(generalOrder, a.type) - orderOf(generalOrder, b.type));
    return [
      { label: "Requests you're a part of", prefs: involved },
      { label: 'General Project Activity', prefs: general },
    ].filter((g) => g.prefs.length > 0);
  }

  private loadNotifPrefs(): void {
    this.notifPrefsLoading = true;
    this.notifPrefsError = false;
    this.notificationService.getPreferences(this.projectService.projectId || undefined).subscribe({
      next: (prefs) => {
        this.settingsPrefs = prefs.map((p) => ({
          type: p.type,
          label: p.label,
          description: p.description,
          enabled: p.enabled,
          emailEnabled: p.send_emails,
        }));
        this.notifPrefsLoading = false;
        this.notifPrefsLoaded = true;
      },
      error: () => {
        this.notifPrefsError = true;
        this.notifPrefsLoading = false;
      },
    });
  }

  togglePref(pref: NotifPrefVm): void {
    const previous = pref.enabled;
    pref.enabled = !pref.enabled;
    this.savePref(pref, () => {
      pref.enabled = previous;
    });
  }

  toggleEmail(pref: NotifPrefVm): void {
    const previous = pref.emailEnabled;
    pref.emailEnabled = !pref.emailEnabled;
    this.savePref(pref, () => {
      pref.emailEnabled = previous;
    });
  }

  private savePref(pref: NotifPrefVm, revert: () => void): void {
    // Preferences are GLOBAL (apply to every project). Only the watch-all opt-in is
    // legitimately project-scoped. `|| undefined` (not ??): projectId is '' when unset,
    // and the backend treats an omitted `project` as the global row.
    const project = pref.type === 'project.watch_all' ? (this.projectService.projectId || undefined) : undefined;
    this.notificationService
      .updatePreferences([{ type: pref.type, enabled: pref.enabled, send_emails: pref.emailEnabled, project }])
      .subscribe({
        next: (saved) => {
          const persisted = saved?.find((s) => s.type === pref.type);
          if (persisted) {
            pref.enabled = persisted.enabled;
            if (persisted.send_emails !== undefined) {
              pref.emailEnabled = persisted.send_emails;
            }
          }
        },
        error: () => revert(),
      });
  }

  // ── Workspace → General ──────────────────────────────────────
  get workspaceIcon(): string | null {
    return this.workspaceIconPreview ?? this.session.organizationLogo();
  }

  get workspaceInitials(): string {
    return this.session.orgInitials();
  }

  get totalMemberPages(): number {
    return Math.max(1, Math.ceil(this.orgMembers.length / this.pageSize));
  }

  get pagedMembers(): OrgMemberDetailDto[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.orgMembers.slice(start, start + this.pageSize);
  }

  get memberPageRangeLabel(): string {
    if (this.orgMembers.length === 0) return 'No members';
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.orgMembers.length);
    return `Showing ${start}–${end} of ${this.orgMembers.length} members`;
  }

  get memberPageNumbers(): number[] {
    return Array.from({ length: this.totalMemberPages }, (_, i) => i + 1);
  }

  setPage(page: number): void {
    if (page < 1 || page > this.totalMemberPages) return;
    this.currentPage = page;
    this.resolveOrgMemberRoleIdsForPage();
  }

  setPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
  }

  private loadOrgMembers(): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    this.membersLoading = true;
    this.membersError = false;
    this.organizationDashboardService.getOrgMembersDetail(orgId).subscribe({
      next: (res) => {
        this.orgMembers = res.data ?? [];
        this.membersLoading = false;
        this.resolveOrgMemberRoleIdsForPage();
      },
      error: () => {
        this.membersError = true;
        this.membersLoading = false;
      }
    });
  }

  // ── Workspace → General: editable member role dropdown (owner/admin only) ──
  get canEditOrgMemberRoles(): boolean {
    return this.session.isOrganizationAdmin();
  }

  private loadOrgRoles(): void {
    if (this.orgRolesLoaded) return;
    this.orgRolesLoading = true;
    this.projectApiService.getRoles().subscribe({
      next: (res) => {
        this.orgRoleOptions = (res.data ?? [])
          .filter((r) => r.name !== 'owner')
          .map((r) => ({ id: r.id, label: this.roleLabels[r.name] ?? r.name }));
        this.orgRolesLoaded = true;
        this.orgRolesLoading = false;
      },
      error: () => {
        this.orgRolesLoading = false;
      }
    });
  }

  /** Resolves OrgMemberRole ids for the currently visible page of members (skips owner rows and already-cached rows). */
  private resolveOrgMemberRoleIdsForPage(): void {
    if (!this.canEditOrgMemberRoles) return;
    this.pagedMembers
      .filter((m) => m.role !== 'owner' && !this.orgMemberRoleRowIds.has(m.id))
      .forEach((m) => {
        this.organizationDashboardService.getOrgMemberRole(m.id).subscribe({
          next: (res) => {
            if (res?.data) this.orgMemberRoleRowIds.set(m.id, res.data.id);
          },
          error: () => {
            // Unassigned (404) or other error — row stays read-only.
          }
        });
      });
  }

  hasResolvedOrgRoleId(memberId: string): boolean {
    return this.orgMemberRoleRowIds.has(memberId);
  }

  isSavingOrgMemberRole(memberId: string): boolean {
    return this.savingOrgMemberRoleIds.has(memberId);
  }

  orgMemberRoleOptionId(member: OrgMemberDetailDto): string | null {
    return this.orgRoleOptions.find((o) => o.label.toLowerCase() === member.role)?.id ?? null;
  }

  onOrgMemberRoleChange(member: OrgMemberDetailDto, roleId: string | null): void {
    if (!roleId) return;
    const rowId = this.orgMemberRoleRowIds.get(member.id);
    if (!rowId) return;

    const previousRole = member.role;
    const optimisticLabel = this.orgRoleOptions.find((o) => o.id === roleId)?.label.toLowerCase();
    member.role = (optimisticLabel ?? previousRole) as OrgMemberDetailDto['role'];
    this.savingOrgMemberRoleIds.add(member.id);

    this.organizationDashboardService.updateOrgMemberRole(rowId, roleId).subscribe({
      next: (res) => {
        this.savingOrgMemberRoleIds.delete(member.id);
        const confirmedLabel = this.orgRoleOptions.find((o) => o.id === res?.data?.role)?.label.toLowerCase();
        if (confirmedLabel) member.role = confirmedLabel as OrgMemberDetailDto['role'];
        this.toastService.success({ message: 'Member role updated.' });
      },
      error: (err) => {
        this.savingOrgMemberRoleIds.delete(member.id);
        member.role = previousRole;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  private loadDomainJoinSetting(): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    this.domainJoinLoading = true;
    this.organizationDashboardService.getOrganization(orgId).subscribe({
      next: (res) => {
        this.domainJoinLoading = false;
        if (res?.status === 'success' && res.data) {
          this.allowDomainJoin = res.data.allow_domain_join !== false;
          this.linkJoinEnabled = !!res.data.link_join_enabled;
          this.workspaceInvitationCode = res.data.invitation_code ?? null;
        }
      },
      error: () => {
        this.domainJoinLoading = false;
      }
    });
  }

  toggleLinkJoin(): void {
    const orgId = this.session.organizationId();
    if (!orgId || this.isUpdatingLinkJoin) return;

    const newValue = !this.linkJoinEnabled;
    this.linkJoinEnabled = newValue;
    this.isUpdatingLinkJoin = true;

    this.organizationDashboardService.updateOrganization(orgId, { link_join_enabled: newValue }).subscribe({
      next: (res) => {
        this.isUpdatingLinkJoin = false;
        if (res?.status === 'success' && res.data) {
          this.linkJoinEnabled = !!res.data.link_join_enabled;
          this.workspaceInvitationCode = res.data.invitation_code ?? null;
        }
      },
      error: (err) => {
        this.isUpdatingLinkJoin = false;
        this.linkJoinEnabled = !newValue;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  copyWorkspaceInviteLink(): void {
    if (!this.workspaceInvitationCode) return;
    const url = `${window.location.origin}/join?code=${this.workspaceInvitationCode}`;
    navigator.clipboard.writeText(url).then(
      () => this.toastService.success({ message: 'Invite link copied.' }),
      () => this.toastService.error({ message: 'Could not copy the link.' })
    );
  }

  toggleDomainJoin(): void {
    const orgId = this.session.organizationId();
    if (!orgId || this.isUpdatingDomainJoin) return;

    const newValue = !this.allowDomainJoin;
    this.allowDomainJoin = newValue;
    this.isUpdatingDomainJoin = true;

    this.organizationDashboardService.updateOrganization(orgId, { allow_domain_join: newValue }).subscribe({
      next: (res) => {
        this.isUpdatingDomainJoin = false;
        if (res?.status === 'success' && res.data) {
          this.allowDomainJoin = res.data.allow_domain_join !== false;
        }
      },
      error: (err) => {
        this.isUpdatingDomainJoin = false;
        this.allowDomainJoin = !newValue;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  saveWorkspaceName(): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    const value = this.workspaceName.trim();
    const previousValue = this.session.organizationName();
    if (value === previousValue || !value) {
      this.workspaceName = previousValue;
      return;
    }

    this.organizationDashboardService.updateOrganization(orgId, { name: value }).subscribe({
      next: () => {
        this.session.patchOrganization({ name: value });
        this.toastService.success({ message: 'Workspace updated.' });
      },
      error: (err) => {
        this.workspaceName = previousValue;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  onWorkspaceIconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      this.uploadWorkspaceIcon(file);
    }
  }

  private uploadWorkspaceIcon(file: File): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    if (!file.type.startsWith('image/')) {
      this.toastService.error({ message: 'Please choose an image file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => (this.workspaceIconPreview = e.target?.result as string);
    reader.readAsDataURL(file);

    this.isUploadingIcon = true;
    this.organizationDashboardService
      .getPresignedUploadUrl({ filename: file.name, content_type: file.type, ticket_id: '', prefix: 'workspaces', organization_id: orgId })
      .subscribe({
        next: (resp) => {
          if (resp.status === 'success' && resp.data) {
            const key = resp.data.key;
            this.organizationDashboardService.uploadFileToPresignedUrl(resp.data.upload_url, file).subscribe({
              next: () => {
                this.organizationDashboardService.updateOrganization(orgId, { logo_key: key }).subscribe({
                  next: (updateResp) => {
                    const logo = updateResp.data?.logo ?? null;
                    this.session.patchOrganization({ logo });
                    this.workspaceIconPreview = logo;
                    this.isUploadingIcon = false;
                    this.toastService.success({ message: 'Workspace icon updated.' });
                  },
                  error: (err) => {
                    this.isUploadingIcon = false;
                    this.workspaceIconPreview = null;
                    this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
                  }
                });
              },
              error: (err) => {
                this.isUploadingIcon = false;
                this.workspaceIconPreview = null;
                this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
              }
            });
          } else {
            this.isUploadingIcon = false;
            this.workspaceIconPreview = null;
            this.toastService.error({ message: 'Could not start the upload. Please try again.' });
          }
        },
        error: (err) => {
          this.isUploadingIcon = false;
          this.workspaceIconPreview = null;
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        }
      });
  }

  // ── Projects → General ────────────────────────────────────────
  private lastSavedProjectName = '';

  get selectedProject(): ProjectsDataDto | undefined {
    return this.projects.find((p) => p.id === this.selectedProjectId);
  }

  get selectedProjectName(): string {
    return this.selectedProject?.name ?? '';
  }

  get projectIcon(): string | null {
    return this.projectIconPreview ?? this.selectedProject?.logo ?? null;
  }

  get projectInitials(): string {
    const name = this.projectName || this.selectedProjectName;
    if (!name) return '';
    const words = name.trim().split(' ');
    if (words.length > 1) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  get activeProjectMembers(): ProjectMemberDto[] {
    return this.projectMembers.filter((m) => m.status === 'active');
  }

  get defaultAssigneeMember(): ProjectMemberDto | undefined {
    return this.projectMembers.find((m) => m.user.user_id === this.defaultAssigneeId);
  }

  get projectMemberTotalPages(): number {
    return Math.max(1, Math.ceil(this.projectMembersDetail.length / this.projectMemberPageSize));
  }

  get pagedProjectMembers(): ProjectMemberDetailDto[] {
    const start = (this.projectMemberCurrentPage - 1) * this.projectMemberPageSize;
    return this.projectMembersDetail.slice(start, start + this.projectMemberPageSize);
  }

  get projectMemberPageRangeLabel(): string {
    if (this.projectMembersDetail.length === 0) return 'No members';
    const start = (this.projectMemberCurrentPage - 1) * this.projectMemberPageSize + 1;
    const end = Math.min(this.projectMemberCurrentPage * this.projectMemberPageSize, this.projectMembersDetail.length);
    return `Showing ${start}–${end} of ${this.projectMembersDetail.length} members`;
  }

  get projectMemberPageNumbers(): number[] {
    return Array.from({ length: this.projectMemberTotalPages }, (_, i) => i + 1);
  }

  setProjectMemberPage(page: number): void {
    if (page < 1 || page > this.projectMemberTotalPages) return;
    this.projectMemberCurrentPage = page;
    this.resolveMemberRoleIdsForPage();
  }

  setProjectMemberPageSize(size: number): void {
    this.projectMemberPageSize = size;
    this.projectMemberCurrentPage = 1;
  }

  selectProject(project: ProjectsDataDto): void {
    this.selectedProjectId = project.id;
    this.projectName = project.name;
    this.lastSavedProjectName = project.name;
    this.projectIconPreview = null;
    this.defaultAssigneeId = null;
    this.projectMembers = [];
    this.projectMembersDetail = [];
    this.projectMemberCurrentPage = 1;
    this.gchatPickerOpen = false;
    this.projectLinkJoinEnabled = false;
    this.projectInvitationCode = null;
    this.projectDefaultRole = 'member';
    this.memberRoleRowIds.clear();
    this.savingMemberRoleIds.clear();

    this.loadProjectDetail(project.id);
    this.loadProjectMembers(project.id);
    this.loadProjectMembersDetail(project.id);
    this.loadProjectLinkSettings(project.id);
    this.loadIntegrationStatuses(project.id);
    this.loadProjectRoles();
    this.projectMembershipService.loadForProject(project.id).subscribe();
  }

  private syncProjectListEntry(projectId: string, patch: Partial<ProjectsDataDto>): void {
    const entry = this.projects.find((p) => p.id === projectId);
    if (entry) {
      Object.assign(entry, patch);
    }
  }

  private loadProjectDetail(projectId: string): void {
    this.projectDetailLoading = true;
    this.projectApiService.getProject(projectId).subscribe({
      next: (res) => {
        this.projectDetailLoading = false;
        if (res?.status === 'success' && res.data) {
          this.projectName = res.data.name;
          this.lastSavedProjectName = res.data.name;
          this.defaultAssigneeId = res.data.default_assignee?.user_id ?? null;
        }
      },
      error: () => {
        this.projectDetailLoading = false;
      }
    });
  }

  private loadProjectMembers(projectId: string): void {
    this.projectApiService.getProjectMembers(projectId).subscribe({
      next: (res) => {
        this.projectMembers = res.data ?? [];
      },
      error: () => {
        this.projectMembers = [];
      }
    });
  }

  private loadProjectMembersDetail(projectId: string): void {
    this.projectMembersLoading = true;
    this.projectMembersError = false;
    this.projectApiService.getProjectMembersDetail(projectId).subscribe({
      next: (res) => {
        this.projectMembersDetail = res.data ?? [];
        this.projectMembersLoading = false;
        this.resolveMemberRoleIdsForPage();
      },
      error: () => {
        this.projectMembersError = true;
        this.projectMembersLoading = false;
      }
    });
  }

  // ── Projects → General: editable member role dropdown ─────────
  get canEditMemberRoles(): boolean {
    return this.projectMembershipService.projectRole === 'admin';
  }

  private loadProjectRoles(): void {
    if (this.projectRolesLoaded) return;
    this.projectRolesLoading = true;
    this.projectApiService.getProjectRoles().subscribe({
      next: (res) => {
        this.projectRoleOptions = (res.data ?? []).map((r) => ({
          id: r.id,
          label: this.roleLabels[r.name] ?? r.name,
        }));
        this.projectRolesLoaded = true;
        this.projectRolesLoading = false;
      },
      error: () => {
        this.projectRolesLoading = false;
      }
    });
  }

  /** Resolves ProjectMemberRole ids for the currently visible page of members (skips already-cached rows). */
  private resolveMemberRoleIdsForPage(): void {
    if (!this.canEditMemberRoles) return;
    this.pagedProjectMembers
      .filter((m) => !this.memberRoleRowIds.has(m.id))
      .forEach((m) => {
        this.projectApiService.getProjectMemberRole(m.id).subscribe({
          next: (res) => {
            if (res?.data) this.memberRoleRowIds.set(m.id, res.data.id);
          },
          error: () => {
            // Unassigned (404) or other error — row stays read-only.
          }
        });
      });
  }

  hasResolvedRoleId(memberId: string): boolean {
    return this.memberRoleRowIds.has(memberId);
  }

  isSavingMemberRole(memberId: string): boolean {
    return this.savingMemberRoleIds.has(memberId);
  }

  memberRoleOptionId(member: ProjectMemberDetailDto): string | null {
    return this.projectRoleOptions.find((o) => o.label.toLowerCase() === member.role)?.id ?? null;
  }

  onMemberRoleChange(member: ProjectMemberDetailDto, roleId: string | null): void {
    if (!roleId) return;
    const rowId = this.memberRoleRowIds.get(member.id);
    if (!rowId) return;

    const previousRole = member.role;
    const optimisticLabel = this.projectRoleOptions.find((o) => o.id === roleId)?.label.toLowerCase();
    member.role = (optimisticLabel ?? previousRole) as ProjectMemberDetailDto['role'];
    this.savingMemberRoleIds.add(member.id);

    this.projectApiService.updateProjectMemberRole(rowId, roleId).subscribe({
      next: (res) => {
        this.savingMemberRoleIds.delete(member.id);
        const confirmedLabel = this.projectRoleOptions.find((o) => o.id === res?.data?.project_role)?.label.toLowerCase();
        if (confirmedLabel) member.role = confirmedLabel as ProjectMemberDetailDto['role'];
        this.toastService.success({ message: 'Member role updated.' });
      },
      error: (err) => {
        this.savingMemberRoleIds.delete(member.id);
        member.role = previousRole;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  private loadProjectLinkSettings(projectId: string): void {
    this.projectApiService.getProjectLinkSettings(projectId).subscribe({
      next: (res) => {
        if (res?.status === 'success' && res.data) {
          this.projectLinkJoinEnabled = res.data.link_join_enabled;
          this.projectInvitationCode = res.data.invitation_code;
          if (res.data.default_role_name === null) {
            // Server defaults to 'viewer' until a role is explicitly set; persist the
            // UI's displayed default ('member') so it matches what new joiners actually get.
            this.projectDefaultRole = 'member';
            this.persistProjectDefaultRole(projectId, 'member');
          } else {
            this.projectDefaultRole = res.data.default_role_name;
          }
        }
      },
      error: () => {
        // Leave defaults — the row will just show as disabled until the user retries by reopening.
      }
    });
  }

  private persistProjectDefaultRole(projectId: string, role: 'admin' | 'member' | 'viewer'): void {
    this.projectApiService.updateProjectLinkSettings(projectId, { default_role: role }).subscribe({
      next: (res) => {
        if (res?.status === 'success' && res.data) {
          this.projectDefaultRole = res.data.default_role_name ?? role;
        }
      },
      error: () => {
        // Best-effort sync; user can still set it explicitly via the dropdown.
      }
    });
  }

  saveProjectName(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    const value = this.projectName.trim();
    if (!value || value === this.lastSavedProjectName) {
      this.projectName = this.lastSavedProjectName;
      return;
    }

    this.projectApiService.patchProject(projectId, { name: value }).subscribe({
      next: () => {
        this.lastSavedProjectName = value;
        this.syncProjectListEntry(projectId, { name: value });
        this.toastService.success({ message: 'Project updated.' });
      },
      error: (err) => {
        this.projectName = this.lastSavedProjectName;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  onProjectIconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      this.uploadProjectIcon(file);
    }
  }

  private uploadProjectIcon(file: File): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    if (!file.type.startsWith('image/')) {
      this.toastService.error({ message: 'Please choose an image file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => (this.projectIconPreview = e.target?.result as string);
    reader.readAsDataURL(file);

    this.isUploadingProjectIcon = true;
    this.requestService
      .getPresignedUploadUrl({ filename: file.name, content_type: file.type, ticket_id: '', prefix: 'projects', project_id: projectId })
      .subscribe({
        next: (resp) => {
          if (resp.status === 'success' && resp.data) {
            const key = resp.data.key;
            this.requestService.uploadFileToPresignedUrl(resp.data.upload_url, file).subscribe({
              next: () => {
                this.projectApiService.patchProject(projectId, { logo_key: key }).subscribe({
                  next: (updateResp) => {
                    const logo = updateResp.data?.logo ?? '';
                    this.syncProjectListEntry(projectId, { logo });
                    this.projectIconPreview = logo;
                    this.isUploadingProjectIcon = false;
                    this.toastService.success({ message: 'Project icon updated.' });
                  },
                  error: (err) => {
                    this.isUploadingProjectIcon = false;
                    this.projectIconPreview = null;
                    this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
                  }
                });
              },
              error: (err) => {
                this.isUploadingProjectIcon = false;
                this.projectIconPreview = null;
                this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
              }
            });
          } else {
            this.isUploadingProjectIcon = false;
            this.projectIconPreview = null;
            this.toastService.error({ message: 'Could not start the upload. Please try again.' });
          }
        },
        error: (err) => {
          this.isUploadingProjectIcon = false;
          this.projectIconPreview = null;
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        }
      });
  }

  setDefaultAssignee(memberId: string): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    const previous = this.defaultAssigneeId;
    this.defaultAssigneeId = memberId;
    this.isSavingDefaultAssignee = true;

    this.projectApiService.patchProject(projectId, { default_assignee_id: memberId }).subscribe({
      next: () => {
        this.isSavingDefaultAssignee = false;
        this.toastService.success({ message: 'Default assignee updated.' });
      },
      error: (err) => {
        this.isSavingDefaultAssignee = false;
        this.defaultAssigneeId = previous;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  toggleMemberMenu(memberId: string): void {
    this.openMemberMenuId = this.openMemberMenuId === memberId ? null : memberId;
  }

  /** Admins can remove members/viewers, but not other admins. */
  canRemoveProjectMember(member: ProjectMemberDetailDto): boolean {
    return this.projectMembershipService.canRemoveMember({ project_role: member.role ?? undefined });
  }

  requestRemoveMember(member: ProjectMemberDetailDto): void {
    this.openMemberMenuId = null;
    this.removeMemberPendingTarget = member;
  }

  cancelRemoveMember(): void {
    this.removeMemberPendingTarget = null;
  }

  confirmRemoveMember(): void {
    const member = this.removeMemberPendingTarget;
    if (!member) return;
    this.removeMemberPendingTarget = null;
    this.removeProjectMember(member);
  }

  private removeProjectMember(member: ProjectMemberDetailDto): void {
    this.openMemberMenuId = null;
    this.projectApiService.deleteProjectMember(member.id).subscribe({
      next: () => {
        this.projectMembersDetail = this.projectMembersDetail.filter((m) => m.id !== member.id);
        this.projectMembers = this.projectMembers.filter((m) => m.id !== member.id);
        if (this.pagedProjectMembers.length === 0 && this.projectMemberCurrentPage > 1) {
          this.projectMemberCurrentPage--;
        }
        this.toastService.success({ message: 'Member removed.' });
      },
      error: (err) => {
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  // ── Projects → Danger zone: delete project ─────────────────────
  get canDeleteProject(): boolean {
    return this.projectMembershipService.canDeleteProject;
  }

  get canConfirmDeleteProject(): boolean {
    return this.deleteProjectConfirmText.trim().toUpperCase() === this.selectedProjectName.trim().toUpperCase();
  }

  openDeleteProjectConfirm(): void {
    this.deleteProjectConfirmOpen = true;
    this.deleteProjectConfirmText = '';
    this.deleteProjectError = null;
  }

  closeDeleteProjectConfirm(): void {
    if (this.isDeletingProject) return;
    this.deleteProjectConfirmOpen = false;
    this.deleteProjectConfirmText = '';
    this.deleteProjectError = null;
  }

  confirmDeleteProject(): void {
    const projectId = this.selectedProjectId;
    if (!projectId || !this.canConfirmDeleteProject || this.isDeletingProject) return;

    this.isDeletingProject = true;
    this.deleteProjectError = null;
    this.projectApiService.deleteProject(projectId).subscribe({
      next: () => {
        this.isDeletingProject = false;
        this.deleteProjectConfirmOpen = false;
        this.toastService.success({ message: 'Project deleted.' });
        this.projectRemoved.emit(projectId);
        this.closeModal.emit();
      },
      error: (err) => {
        this.isDeletingProject = false;
        this.deleteProjectError = this.errorHandler.getUserFriendlyErrorMessage(err);
      }
    });
  }

  // ── Projects → Danger zone: leave project ───────────────────────
  openLeaveProjectConfirm(): void {
    this.leaveProjectConfirmOpen = true;
  }

  closeLeaveProjectConfirm(): void {
    if (this.isLeavingProject) return;
    this.leaveProjectConfirmOpen = false;
  }

  confirmLeaveProject(): void {
    const projectId = this.selectedProjectId;
    if (!projectId || this.isLeavingProject) return;

    this.isLeavingProject = true;
    this.projectApiService.leaveProject(projectId).subscribe({
      next: () => {
        this.isLeavingProject = false;
        this.leaveProjectConfirmOpen = false;
        this.toastService.success({ message: 'You left the project.' });
        this.projectRemoved.emit(projectId);
        this.closeModal.emit();
      },
      error: (err) => {
        this.isLeavingProject = false;
        this.leaveProjectConfirmOpen = false;
        // The sole-admin 403 message from the backend is already actionable — show it as-is.
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  // ── Projects → General: Invite link ───────────────────────────
  toggleProjectLinkJoin(): void {
    const projectId = this.selectedProjectId;
    if (!projectId || this.isUpdatingProjectLinkJoin) return;

    const newValue = !this.projectLinkJoinEnabled;
    this.projectLinkJoinEnabled = newValue;
    this.isUpdatingProjectLinkJoin = true;

    this.projectApiService.updateProjectLinkSettings(projectId, { link_join_enabled: newValue }).subscribe({
      next: (res) => {
        this.isUpdatingProjectLinkJoin = false;
        if (res?.status === 'success' && res.data) {
          this.projectLinkJoinEnabled = res.data.link_join_enabled;
          this.projectInvitationCode = res.data.invitation_code;
        }
      },
      error: (err) => {
        this.isUpdatingProjectLinkJoin = false;
        this.projectLinkJoinEnabled = !newValue;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  copyProjectInviteLink(): void {
    if (!this.projectLinkJoinEnabled || !this.projectInvitationCode) return;
    const url = `${window.location.origin}/projects/join?code=${this.projectInvitationCode}`;
    navigator.clipboard.writeText(url).then(
      () => this.toastService.success({ message: 'Invite link copied.' }),
      () => this.toastService.error({ message: 'Could not copy the link.' })
    );
  }

  regenerateProjectInviteLink(): void {
    const projectId = this.selectedProjectId;
    if (!projectId || this.isRegeneratingProjectLink) return;

    this.isRegeneratingProjectLink = true;
    this.projectApiService.regenerateProjectLink(projectId).subscribe({
      next: (res) => {
        this.isRegeneratingProjectLink = false;
        if (res?.status === 'success' && res.data) {
          this.projectInvitationCode = res.data.invitation_code;
          this.toastService.success({ message: 'Invite link regenerated.' });
        }
      },
      error: (err) => {
        this.isRegeneratingProjectLink = false;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  onProjectDefaultRoleChange(roleId: string | null): void {
    const projectId = this.selectedProjectId;
    if (!projectId || !roleId) return;

    const role = roleId as 'admin' | 'member' | 'viewer';
    const previous = this.projectDefaultRole;
    this.projectDefaultRole = role;
    this.isSavingProjectDefaultRole = true;

    this.projectApiService.updateProjectLinkSettings(projectId, { default_role: role }).subscribe({
      next: (res) => {
        this.isSavingProjectDefaultRole = false;
        if (res?.status === 'success' && res.data) {
          this.projectDefaultRole = res.data.default_role_name ?? role;
        }
        this.toastService.success({ message: 'Default role updated.' });
      },
      error: (err) => {
        this.isSavingProjectDefaultRole = false;
        this.projectDefaultRole = previous;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  // ── Projects → General: Integrations ──────────────────────────
  private loadIntegrationStatuses(projectId: string): void {
    this.loadSlackStatus(projectId);
    this.loadWebflowStatus(projectId);
    this.loadGchatStatus(projectId);
  }

  private loadSlackStatus(projectId: string): void {
    this.slackLoading = true;
    this.slackService.getProjectStatus(projectId).subscribe({
      next: (res) => {
        this.slackConnected = !!(res.data?.project_installation || res.data?.org_connected);
        this.slackLoading = false;
      },
      error: () => {
        this.slackConnected = false;
        this.slackLoading = false;
      }
    });
  }

  connectSlack(): void {
    const projectId = this.selectedProjectId;
    const orgId = this.session.organizationId();
    if (!projectId || !orgId) return;

    this.slackLoading = true;
    this.slackService.getOAuthInstallUrl(orgId, projectId).subscribe({
      next: (res) => {
        this.slackLoading = false;
        if (res.data?.url) {
          window.open(res.data.url, '_blank');
        }
      },
      error: (err) => {
        this.slackLoading = false;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  disconnectSlack(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    this.slackService.disconnectProjectSlack(projectId).subscribe({
      next: () => {
        this.slackConnected = false;
        this.toastService.success({ message: 'Slack disconnected.' });
      },
      error: (err) => {
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  private loadWebflowStatus(projectId: string): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    this.webflowLoading = true;
    this.webflowService.getProjectStatus(orgId, projectId).subscribe({
      next: (res) => {
        this.webflowConnected = !!res.data?.connected;
        const createdAt = res.data?.installation?.created_at;
        this.webflowConnectedSince = createdAt
          ? new Date(createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
          : '';
        this.webflowLoading = false;
      },
      error: () => {
        this.webflowConnected = false;
        this.webflowLoading = false;
      }
    });
  }

  connectWebflow(): void {
    const projectId = this.selectedProjectId;
    const orgId = this.session.organizationId();
    if (!projectId || !orgId) return;

    this.webflowLoading = true;
    this.webflowService.getOAuthStartUrl(orgId, projectId).subscribe({
      next: (res) => {
        this.webflowLoading = false;
        if (res.data?.url) {
          window.open(res.data.url, '_blank');
        }
      },
      error: (err) => {
        this.webflowLoading = false;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  disconnectWebflow(): void {
    const projectId = this.selectedProjectId;
    const orgId = this.session.organizationId();
    if (!projectId || !orgId) return;

    this.webflowService.disconnectProject(orgId, projectId).subscribe({
      next: () => {
        this.webflowConnected = false;
        this.webflowConnectedSince = '';
        this.toastService.success({ message: 'Webflow disconnected.' });
      },
      error: (err) => {
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  private loadGchatStatus(projectId: string): void {
    this.gchatLoading = true;
    this.googleChatService.getProjectStatus(projectId).subscribe({
      next: (res) => {
        this.gchatConnected = !!res.data?.configured;
        this.gchatLoading = false;
      },
      error: () => {
        this.gchatConnected = false;
        this.gchatLoading = false;
      }
    });
  }

  openGchatPicker(): void {
    this.gchatPickerOpen = true;
    if (this.gchatSpaces.length === 0) {
      this.gchatSpacesLoading = true;
      this.googleChatService.listSpaces().subscribe({
        next: (res) => {
          this.gchatSpaces = res.data?.spaces ?? [];
          this.gchatSpacesLoading = false;
        },
        error: () => {
          this.gchatSpacesLoading = false;
          this.toastService.error({ message: 'Google Chat is not set up for this organization yet.' });
        }
      });
    }
  }

  cancelGchatPicker(): void {
    this.gchatPickerOpen = false;
    this.gchatSelectedSpaceName = '';
  }

  connectGchat(): void {
    const projectId = this.selectedProjectId;
    const space = this.gchatSpaces.find((s) => s.name === this.gchatSelectedSpaceName);
    if (!projectId || !space) return;

    this.gchatConnecting = true;
    this.googleChatService.setProjectSpace(projectId, { space_name: space.name, space_display_name: space.displayName }).subscribe({
      next: () => {
        this.gchatConnecting = false;
        this.gchatConnected = true;
        this.gchatPickerOpen = false;
        this.toastService.success({ message: 'Google Chat connected.' });
      },
      error: (err) => {
        this.gchatConnecting = false;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  disconnectGchat(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    this.googleChatService.removeProjectSpace(projectId).subscribe({
      next: () => {
        this.gchatConnected = false;
        this.toastService.success({ message: 'Google Chat disconnected.' });
      },
      error: (err) => {
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  get userInitials(): string {
    return this.session.userInitials();
  }

  get userAvatar(): string | null {
    return this.avatarPreview ?? this.session.userAvatar();
  }

  get userName(): string {
    return this.session.userName();
  }

  get userEmail(): string {
    return this.session.userEmail();
  }

  onClose(): void {
    this.passwordFormOpen = false;
    this.closeModal.emit();
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  saveName(field: 'first_name' | 'last_name'): void {
    const user = this.session.profile();
    if (!user) return;

    const value = field === 'first_name' ? this.firstName.trim() : this.lastName.trim();
    const previousValue = user[field];
    if (value === previousValue || !value) {
      this.firstName = user.first_name;
      this.lastName = user.last_name;
      return;
    }

    this.onboardingService.updateUserProfile(user.id, { [field]: value }).subscribe({
      next: () => {
        this.session.patchProfile({ [field]: value });
        this.toastService.success({ message: 'Profile updated.' });
      },
      error: () => {
        this.firstName = user.first_name;
        this.lastName = user.last_name;
        this.toastService.error({ message: 'Could not update your profile. Please try again.' });
      }
    });
  }

  onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      this.uploadAvatar(file);
    }
  }

  private uploadAvatar(file: File): void {
    const user = this.session.profile();
    if (!user) return;

    if (!file.type.startsWith('image/')) {
      this.toastService.error({ message: 'Please choose an image file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => (this.avatarPreview = e.target?.result as string);
    reader.readAsDataURL(file);

    this.isUploadingAvatar = true;
    this.onboardingService.getPresignedUploadUrl({ filename: file.name, content_type: file.type, prefix: 'avatars' }).subscribe({
      next: (resp) => {
        if (resp.status === 'success' && resp.data) {
          const key = resp.data.key;
          this.onboardingService.uploadFileToPresignedUrl(resp.data.upload_url, file).subscribe({
            next: () => {
              this.onboardingService.updateUserProfile(user.id, { avatar_key: key }).subscribe({
                next: (updateResp) => {
                  const avatar = updateResp.data?.avatar ?? null;
                  this.session.patchProfile({ avatar });
                  this.avatarPreview = avatar;
                  this.isUploadingAvatar = false;
                  this.toastService.success({ message: 'Profile picture updated.' });
                },
                error: (err) => {
                  this.isUploadingAvatar = false;
                  this.avatarPreview = null;
                  this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
                }
              });
            },
            error: (err) => {
              this.isUploadingAvatar = false;
              this.avatarPreview = null;
              this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
            }
          });
        } else {
          this.isUploadingAvatar = false;
          this.avatarPreview = null;
          this.toastService.error({ message: 'Could not start the upload. Please try again.' });
        }
      },
      error: (err) => {
        this.isUploadingAvatar = false;
        this.avatarPreview = null;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      }
    });
  }

  togglePasswordForm(): void {
    this.passwordFormOpen = !this.passwordFormOpen;
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
  }

  submitPasswordChange(): void {
    if (!this.currentPassword || !this.newPassword || !this.confirmPassword) {
      this.toastService.error({ message: 'Please fill in all password fields.' });
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.toastService.error({ message: 'New password and confirmation do not match.' });
      return;
    }

    this.passwordSubmitting = true;
    this.dashboardService.updatePassword(this.currentPassword, this.newPassword).subscribe({
      next: () => {
        this.passwordSubmitting = false;
        this.toastService.success({ message: 'Password updated.' });
        this.togglePasswordForm();
      },
      error: () => {
        this.passwordSubmitting = false;
        this.toastService.error({ message: 'Could not update your password. Please try again.' });
      }
    });
  }
}
